import React, { useMemo, useState } from 'react';
import { Checkbox, CheckPicker, DateRangePicker, Input, Message, Placeholder, SelectPicker } from 'rsuite';
import DatePicker from '../components/shared/DatePicker';
import { Button, FilterField, FilterToolbar, Modal, renderSelectAllFooter, useLastPresent } from '@nsi-advisory-services/components';
import { showToast } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import ExpenseList from '../components/expenses/ExpenseList';
import ExpensePayrollKanban from '../components/expenses/ExpensePayrollKanban';
import ExpenseMarkPaidModal from '../components/expenses/ExpenseMarkPaidModal';
import ExpenseReceiptModal from '../components/expenses/ExpenseReceiptModal';
import ApprovalReviewModal from '../components/timeoff/ApprovalReviewModal';
import PayPeriodSelect from '../components/shared/PayPeriodSelect';
import PageHeader from '../components/shared/PageHeader';
import { useApproveExpense, useCommentExpense, useExpenses, useMarkExpenseInvoiced, useMarkExpensePaid, useRejectExpense, useRevertExpenseStage, useSetExpenseInvoiceCap, useSetExpenseQboReceipt } from '../hooks/useQueries';
import {
  toStartOfDay,
  toDateOnlyString,
  getPayPeriodOptions,
} from '../utils/payPeriods';
import { getCategoryLabel } from '../utils/expenseCategories';
import { formatCurrency, formatCurrencyTyping, sanitizeAmount } from '../utils/currency';
import { STAGE_AWAITING, STAGE_INVOICED, STAGE_PAID, STAGE_PENDING, getExpenseStage, getStageTitle } from '../utils/expenseWorkflow';
import './ApprovalsPage.css';
import { renderEmployeeSelection } from '../components/shared/employeeSelection';

const STALE_APPROVAL_TOAST = 'This expense has been modified. The list has been refreshed.';

// Through toStartOfDay so a date-only string stays the calendar date it is,
// rather than being pulled a day backwards by the local timezone.
const formatDate = (value) => {
  const date = toStartOfDay(value);
  return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

// Remembering the choice matters because the two views answer different
// questions — the board for working the queue, the tables for reading it — and
// people tend to stay in one.
//
// The key carries a version because the default flipped from tables to the
// board: anyone who used the page before has "table" saved from back when it
// was the only thing to land on, and would never see the new default. The
// bumped key drops those stale preferences once; choices made from here on are
// remembered as before.
const VIEW_MODE_STORAGE_KEY = 'expensePayrollViewMode.v2';

// The board is the default; the tables are the deliberate switch away from it.
const readStoredViewMode = () => {
  try {
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored === 'table' ? 'table' : 'board';
  } catch {
    return 'board';
  }
};

// The server refuses to approve, reject or comment on an expense that is no
// longer pending; any of those means someone else got there first, so match the
// shared prefix rather than each verb.
const isStaleExpenseDecisionError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('only pending expenses can be')
    || message.includes('expense not found')
  );
};

const showApprovalToast = (type, content) => showToast(type, content, { duration: 5000 });

export default function ExpensePayrollViewPage() {
  const auth = useAuth();
  const currentEmployeeId = String(auth.employee?.id || '');
  const canManagePayroll = auth.isFinance() || auth.isAdmin() || auth.hasPermission('manage_payroll');

  const expensesQuery = useExpenses({ enabled: canManagePayroll });
  const approveExpenseMutation = useApproveExpense();
  const rejectExpenseMutation = useRejectExpense();
  const commentExpenseMutation = useCommentExpense();
  const markExpensePaidMutation = useMarkExpensePaid();
  const markExpenseInvoicedMutation = useMarkExpenseInvoiced();
  const setExpenseInvoiceCapMutation = useSetExpenseInvoiceCap();
  const revertExpenseStageMutation = useRevertExpenseStage();
  const setQboReceiptMutation = useSetExpenseQboReceipt();

  // Empty means "no restriction" for all three, which is why they open on
  // "All …" rather than with everything ticked: nothing to undo to see the
  // whole queue again.
  const [vendorFilter, setVendorFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState([]);
  const [qboReceiptFilter, setQboReceiptFilter] = useState('all');
  const [activeRecord, setActiveRecord] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [paidExpense, setPaidExpense] = useState(null);
  const [paidOnDate, setPaidOnDate] = useState(null);
  const [invoicedExpense, setInvoicedExpense] = useState(null);
  const invoicedShown = useLastPresent(invoicedExpense); // keeps the dialog steady while it fades out
  const [invoicedOnDate, setInvoicedOnDate] = useState(null);
  // Raw and display kept apart, the same pair the expense form uses: the field
  // formats as money on every keystroke, and a half-typed "12." has to survive
  // that round trip or the decimal point is eaten as fast as it is typed.
  const [invoiceCapped, setInvoiceCapped] = useState(false);
  const [invoiceCapAmount, setInvoiceCapAmount] = useState('');
  const [invoiceCapDisplay, setInvoiceCapDisplay] = useState('');
  const [viewMode, setViewMode] = useState(readStoredViewMode);

  const payPeriodOptions = useMemo(() => getPayPeriodOptions(), []);
  // Opens on everything. A null window is the "all dates" state the filter
  // already understands, so payroll starts from the full picture and narrows
  // down, rather than opening inside the current pay period with no hint that
  // anything outside it exists.
  const [payPeriodStart, setPayPeriodStart] = useState(null);
  const [payPeriodEnd, setPayPeriodEnd] = useState(null);
  const [payPeriodFilter, setPayPeriodFilter] = useState(null);

  // The range picker and the pay-period picker are two views of one window, so
  // the dates stay the source of truth and the period is derived from them.
  const dateRange = useMemo(
    () => (payPeriodStart && payPeriodEnd ? [payPeriodStart, payPeriodEnd] : null),
    [payPeriodStart, payPeriodEnd],
  );

  const selectPayPeriod = (nextValue) => {
    if (!nextValue) return;
    const [startRaw, endRaw] = String(nextValue).split('|');
    const start = toStartOfDay(startRaw);
    const end = toStartOfDay(endRaw);
    if (!start || !end) return;

    setPayPeriodFilter(String(nextValue));
    setPayPeriodStart(start);
    setPayPeriodEnd(end);
  };

  const applyDateRange = (range) => {
    const [rawStart, rawEnd] = Array.isArray(range) ? range : [];
    const start = toStartOfDay(rawStart);
    const end = toStartOfDay(rawEnd);

    setPayPeriodStart(start);
    setPayPeriodEnd(end);

    // A hand-picked range that lands exactly on a pay period is still that pay
    // period; anything else leaves the picker showing "Custom".
    const asPeriod = start && end ? `${toDateOnlyString(start)}|${toDateOnlyString(end)}` : null;
    const matched = asPeriod && payPeriodOptions.some((option) => option.value === asPeriod);
    setPayPeriodFilter(matched ? asPeriod : null);
  };

  const visibleBaseExpenses = useMemo(() => {
    return expensesQuery.data || [];
  }, [expensesQuery.data]);

  const vendorOptions = useMemo(() => {
    const unique = new Map();
    visibleBaseExpenses.forEach((expense) => {
      const vendor = String(expense?.vendor_name || '').trim();
      if (!vendor) return;
      unique.set(vendor.toLowerCase(), vendor);
    });

    const items = Array.from(unique.values()).sort((a, b) => a.localeCompare(b));
    return items.map((value) => ({ label: value, value }));
  }, [visibleBaseExpenses]);

  const categoryOptions = useMemo(() => {
    const unique = new Map();
    visibleBaseExpenses.forEach((expense) => {
      const category = String(expense?.category || '').trim();
      if (!category) return;
      unique.set(category, { value: category, label: getCategoryLabel(category) });
    });

    return Array.from(unique.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [visibleBaseExpenses]);

  const employeeOptions = useMemo(() => {
    const byId = new Map();
    visibleBaseExpenses.forEach((expense) => {
      const id = String(expense?.employee_id || '');
      if (!id || byId.has(id)) return;
      const first = expense.first_name || expense.employee_first_name || '';
      const last = expense.last_name || expense.employee_last_name || '';
      const email = expense.email || expense.employee_email || '';
      const name = `${first} ${last}`.trim() || 'Unknown Employee';
      byId.set(id, {
        value: id,
        label: [name, email].filter(Boolean).join(' • '),
      });
    });

    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [visibleBaseExpenses]);

  const filteredExpenses = useMemo(() => {
    const start = toStartOfDay(payPeriodStart);
    const end = toStartOfDay(payPeriodEnd);

    // Built once per pass rather than per row: these lists can be long enough
    // that scanning them for every expense is wasted work.
    const vendors = new Set(vendorFilter);
    const categories = new Set(categoryFilter);
    const employees = new Set(employeeFilter.map((id) => String(id)));

    return visibleBaseExpenses.filter((expense) => {
      const vendor = String(expense?.vendor_name || '').trim();
      const category = String(expense?.category || '').trim();
      const employeeId = String(expense?.employee_id || '');
      // Use the full purchase range (tech reimbursements span many months) and
      // fall back to the single purchase_date when range bounds are absent.
      const purchaseStart = toStartOfDay(expense?.purchase_start_date || expense?.purchase_date);
      const purchaseEnd = toStartOfDay(expense?.purchase_end_date || expense?.purchase_date);

      const qboUploaded = Boolean(expense?.receipt_uploaded_to_qbo);

      const matchesVendor = vendors.size === 0 || vendors.has(vendor);
      const matchesCategory = categories.size === 0 || categories.has(category);
      const matchesEmployee = employees.size === 0 || employees.has(employeeId);
      // Include the expense when its date range overlaps the selected window at all.
      const matchesPayPeriod = !start || !end
        || (purchaseStart && purchaseEnd && purchaseStart <= end && purchaseEnd >= start);
      const matchesQbo = qboReceiptFilter === 'all'
        || (qboReceiptFilter === 'yes' && qboUploaded)
        || (qboReceiptFilter === 'no' && !qboUploaded);

      return matchesVendor && matchesCategory && matchesEmployee && matchesPayPeriod && matchesQbo;
    }).sort((a, b) => {
      const aTime = new Date(a?.created_at || 0).getTime();
      const bTime = new Date(b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [visibleBaseExpenses, vendorFilter, categoryFilter, employeeFilter, qboReceiptFilter, payPeriodStart, payPeriodEnd]);

  // Both views read the same four buckets out of utils/expenseWorkflow, so the
  // board and the tables can never disagree about where an expense sits.
  const pendingApprovalExpenses = useMemo(
    () => filteredExpenses.filter((expense) => getExpenseStage(expense) === STAGE_PENDING),
    [filteredExpenses],
  );

  const awaitingPaymentExpenses = useMemo(
    () => filteredExpenses.filter((expense) => getExpenseStage(expense) === STAGE_AWAITING),
    [filteredExpenses],
  );

  const approvedPaidExpenses = useMemo(
    () => filteredExpenses.filter((expense) => getExpenseStage(expense) === STAGE_PAID),
    [filteredExpenses],
  );

  const invoicedExpenses = useMemo(
    () => filteredExpenses.filter((expense) => getExpenseStage(expense) === STAGE_INVOICED),
    [filteredExpenses],
  );

  const openReviewExpense = async (row) => {
    let latestRows = expensesQuery.data || [];
    try {
      const refreshed = await expensesQuery.refetch();
      latestRows = refreshed?.data || latestRows;
    } catch {
      return;
    }

    const latest = latestRows.find((expense) => String(expense?.id) === String(row?.id));
    if (!latest) return;

    setActiveRecord(latest);
    setReviewOpen(true);
  };

  const changeViewMode = (nextMode) => {
    setViewMode(nextMode);
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, nextMode);
    } catch {
      // A browser that refuses storage still gets the toggle, just not the memory.
    }
  };

  const toggleQboReceipt = async (expense, checked) => {
    if (!expense?.id) return;
    try {
      await setQboReceiptMutation.mutateAsync({
        id: expense.id,
        receipt_uploaded_to_qbo: Boolean(checked),
      });
      setActiveRecord((prev) => (
        prev && String(prev.id) === String(expense.id)
          ? { ...prev, receipt_uploaded_to_qbo: Boolean(checked) }
          : prev
      ));
    } catch {
      await expensesQuery.refetch();
    }
  };

  const handlePrintReceipt = () => {
    if (!activeRecord?.id) return;
    setReviewOpen(false);
    setReceiptOpen(true);
  };

  const handleReceiptCancel = () => {
    setReceiptOpen(false);
    if (activeRecord?.id) setReviewOpen(true);
  };

  const submitApprove = async (approval_reason) => {
    if (!activeRecord?.id) return;

    try {
      await approveExpenseMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      setReviewOpen(false);
      setActiveRecord(null);
    } catch (error) {
      if (isStaleExpenseDecisionError(error)) {
        showApprovalToast('warning', STALE_APPROVAL_TOAST);
        setReviewOpen(false);
        setActiveRecord(null);
        await expensesQuery.refetch();
        return;
      }

      showApprovalToast('error', String(error?.message || error || 'Unable to approve expense'));
    }
  };

  // Saves the comment and leaves the expense pending, so it stays in the queue.
  const submitComment = async (approval_reason) => {
    if (!activeRecord?.id) return;

    try {
      await commentExpenseMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      showApprovalToast('success', 'Comment saved');
      setReviewOpen(false);
      setActiveRecord(null);
    } catch (error) {
      if (isStaleExpenseDecisionError(error)) {
        showApprovalToast('warning', STALE_APPROVAL_TOAST);
        setReviewOpen(false);
        setActiveRecord(null);
        await expensesQuery.refetch();
        return;
      }

      showApprovalToast('error', String(error?.message || error || 'Unable to save comment'));
    }
  };

  const submitReject = async (approval_reason) => {
    if (!activeRecord?.id) return;

    try {
      await rejectExpenseMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      setReviewOpen(false);
      setActiveRecord(null);
    } catch (error) {
      if (isStaleExpenseDecisionError(error)) {
        showApprovalToast('warning', STALE_APPROVAL_TOAST);
        setReviewOpen(false);
        setActiveRecord(null);
        await expensesQuery.refetch();
        return;
      }

      showApprovalToast('error', String(error?.message || error || 'Unable to reject expense'));
    }
  };

  const openMarkPaid = (expense) => {
    if (!expense?.id) return;
    setPaidExpense(expense);
    setPaidOnDate(new Date());
  };

  const closeMarkPaid = () => {
    setPaidExpense(null);
    setPaidOnDate(null);
  };

  const submitMarkPaid = async () => {
    if (!paidExpense?.id || !paidOnDate) return;

    try {
      const paidOn = toDateOnlyString(paidOnDate);
      await markExpensePaidMutation.mutateAsync({ id: paidExpense.id, paid_on: paidOn });
      // Marked from inside the approval details, those are still open behind
      // this dialog and would go on describing an unpaid expense otherwise.
      setActiveRecord((prev) => (
        prev && String(prev.id) === String(paidExpense.id) ? { ...prev, paid_on: paidOn } : prev
      ));
      closeMarkPaid();
    } catch {
      await expensesQuery.refetch();
    }
  };

  // The board's own dialog is the confirmation — it names what the revert
  // erases and the override button is the second click — so this just runs it.
  const revertExpenseStage = async (expense, stage) => {
    if (!expense?.id || !stage) return;

    try {
      await revertExpenseStageMutation.mutateAsync({ id: expense.id, stage });
      showApprovalToast('success', `Moved back to ${getStageTitle(stage)}.`);
    } catch (error) {
      showApprovalToast('error', String(error?.message || error || 'Unable to move this expense back'));
      await expensesQuery.refetch();
    }
  };

  // Mirrors the mark-paid flow: the board only decides the expense may move,
  // and the date it moved on is asked for here.
  //
  // The same dialog reopens on an expense that is already invoiced, which is
  // how a mistyped cap gets corrected — see isCapEdit below. Nothing has to be
  // passed in to say which it is: an expense that carries an invoice date has
  // already made the move, so the only thing left to change is the cap.
  const openMarkInvoiced = (expense) => {
    if (!expense?.id) return;
    const existingCap = expense.invoice_cap_amount;
    setInvoicedExpense(expense);
    setInvoicedOnDate(toStartOfDay(expense.invoiced_on) || new Date());
    setInvoiceCapped(existingCap != null);
    setInvoiceCapAmount(existingCap == null ? '' : String(Number(existingCap)));
    setInvoiceCapDisplay(existingCap == null ? '' : formatCurrency(existingCap));
  };

  const closeMarkInvoiced = () => setInvoicedExpense(null);

  // Once the dialog has faded out, so its fields don't empty while it is visible.
  const resetMarkInvoiced = () => {
    setInvoicedOnDate(null);
    setInvoiceCapped(false);
    setInvoiceCapAmount('');
    setInvoiceCapDisplay('');
  };

  const changeInvoiceCap = (value) => {
    const sanitized = sanitizeAmount(value);
    setInvoiceCapAmount(sanitized);
    setInvoiceCapDisplay(formatCurrencyTyping(sanitized));
  };

  // Settles the field on the finished form once the caret leaves: "$1,200." and
  // "$1,200.5" both become "$1,200.50".
  const settleInvoiceCap = () => {
    if (!invoiceCapAmount) {
      setInvoiceCapDisplay('');
      return;
    }
    const numeric = Number(invoiceCapAmount);
    if (!Number.isFinite(numeric)) {
      setInvoiceCapDisplay(formatCurrencyTyping(invoiceCapAmount));
      return;
    }
    setInvoiceCapAmount(String(numeric));
    setInvoiceCapDisplay(formatCurrency(numeric));
  };

  // An expense that already carries an invoice date is in the dialog to have
  // its cap corrected, not to be invoiced again.
  const isCapEdit = Boolean(invoicedShown?.invoiced_on);

  // A cap is what we were allowed to bill, so it can only ever be a reduction —
  // anything above the expense amount is a typo, and the server refuses it too.
  const invoiceCapValue = invoiceCapped && String(invoiceCapAmount).trim() !== ''
    ? Number(invoiceCapAmount)
    : null;
  const invoiceCapMax = Number(invoicedShown?.amount) || 0;
  const invoiceCapError = !invoiceCapped || invoiceCapValue == null
    ? null
    : (!Number.isFinite(invoiceCapValue) || invoiceCapValue < 0
      ? 'Enter an amount of $0.00 or more.'
      : (invoiceCapValue > invoiceCapMax
        ? `The cap cannot be more than the ${formatCurrency(invoiceCapMax)} we paid.`
        : null));
  const canSubmitInvoiced = Boolean(invoicedOnDate)
    && (!invoiceCapped || (invoiceCapValue != null && !invoiceCapError));
  const invoicedSaving = markExpenseInvoicedMutation.isPending || setExpenseInvoiceCapMutation.isPending;

  // Hands the expense over to the review modal, which is where everything the
  // cap dialog does not show still lives.
  const viewInvoicedExpense = () => {
    const expense = invoicedExpense;
    closeMarkInvoiced();
    openReviewExpense(expense);
  };

  const submitMarkInvoiced = async () => {
    if (!invoicedExpense?.id || !canSubmitInvoiced) return;
    const cap = invoiceCapped ? invoiceCapValue : null;

    try {
      if (isCapEdit) {
        await setExpenseInvoiceCapMutation.mutateAsync({ id: invoicedExpense.id, invoice_cap_amount: cap });
      } else {
        await markExpenseInvoicedMutation.mutateAsync({
          id: invoicedExpense.id,
          invoiced_on: toDateOnlyString(invoicedOnDate),
          invoice_cap_amount: cap,
        });
      }
      closeMarkInvoiced();
    } catch (error) {
      showApprovalToast(
        'error',
        String(error?.message || error || (isCapEdit ? 'Unable to update invoice cap' : 'Unable to mark expense invoiced')),
      );
      await expensesQuery.refetch();
    }
  };

  if (auth.isLoading || auth.isResolving) {
    return <Placeholder.Paragraph rows={6} active />;
  }

  if (!canManagePayroll) {
    return (
      <Message type="error" header="Access denied" showIcon>
        You do not have permission to view the payroll view.
      </Message>
    );
  }

  if (expensesQuery.isLoading) {
    return <Placeholder.Paragraph rows={6} active />;
  }

  if (expensesQuery.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(expensesQuery.error?.message || expensesQuery.error)}
      </Message>
    );
  }

  return (
    <div className="approvals-page bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Approvals', to: '/approvals' },
          { label: 'Payroll View' },
        ]}
        icon="bi-cash-stack"
        title="Expense Payroll View"
        backLabel="Back to Approvals"
        actions={(
          <div className="bdc-segment">
            <button
              type="button"
              className={`bdc-btn${viewMode === 'board' ? ' bdc-btn--primary' : ''}`}
              onClick={() => changeViewMode('board')}
              aria-pressed={viewMode === 'board'}
            >
              <i className="bi bi-kanban" aria-hidden="true" />
              Board
            </button>
            <button
              type="button"
              className={`bdc-btn${viewMode === 'table' ? ' bdc-btn--primary' : ''}`}
              onClick={() => changeViewMode('table')}
              aria-pressed={viewMode === 'table'}
            >
              <i className="bi bi-table" aria-hidden="true" />
              Tables
            </button>
          </div>
        )}
      />

      <FilterToolbar>
        {/* countable so a narrow picker says "3 selected" instead of trying to
            fit three vendor names into the width it has. */}
        <FilterField label="Vendors" basis={160} max={260}>
          <CheckPicker
            data={vendorOptions}
            value={vendorFilter}
            onChange={(nextValue) => setVendorFilter(nextValue || [])}
            placeholder="All Vendors"
            countable
            searchable
            preventOverflow
            renderExtraFooter={renderSelectAllFooter(vendorOptions, vendorFilter, setVendorFilter)}
            block
          />
        </FilterField>

        <FilterField label="Categories" basis={160} max={260}>
          <CheckPicker
            data={categoryOptions}
            value={categoryFilter}
            onChange={(nextValue) => setCategoryFilter(nextValue || [])}
            placeholder="All Categories"
            countable
            searchable
            preventOverflow
            renderExtraFooter={renderSelectAllFooter(categoryOptions, categoryFilter, setCategoryFilter)}
            block
          />
        </FilterField>

        <FilterField label="Employees" basis={200} max={320}>
          <CheckPicker
            data={employeeOptions}
            countable={false}
            renderValue={renderEmployeeSelection(employeeOptions)}
            value={employeeFilter}
            onChange={(nextValue) => setEmployeeFilter(nextValue || [])}
            placeholder="All Employees"
            searchable
            preventOverflow
            renderExtraFooter={renderSelectAllFooter(employeeOptions, employeeFilter, setEmployeeFilter)}
            block
          />
        </FilterField>

        <FilterField label="Receipt in QBO" basis={140} max={200}>
          <SelectPicker
            data={[
              { label: 'All', value: 'all' },
              { label: 'Uploaded', value: 'yes' },
              { label: 'Not uploaded', value: 'no' },
            ]}
            value={qboReceiptFilter}
            onChange={(nextValue) => setQboReceiptFilter(nextValue || 'all')}
            cleanable={false}
            searchable={false}
            block
          />
        </FilterField>

        <FilterField label="Pay Period" basis={210} max={300}>
          <PayPeriodSelect
            data={payPeriodOptions}
            value={payPeriodFilter}
            onChange={selectPayPeriod}
            // The range sits right beside it, so the closed picker only needs
            // to name the period, not spell out its dates.
            compact
            searchable
            // Shown whenever the range isn't one of the listed periods. With no
            // range at all — the state this page now opens in — "Custom" would be
            // a lie, so it says what the range picker beside it says.
            placeholder={dateRange ? 'Custom' : 'All Dates'}
            block
          />
        </FilterField>

        <FilterField label="Date Range" basis={230} minWidth={260}>
          <DateRangePicker
            value={dateRange}
            onChange={applyDateRange}
            format="MM/dd/yyyy"
            character=" – "
            placeholder="All Dates"
            isoWeek={false}
            weekStart={0}
            // The pay periods next door are the shortcuts that matter here.
            ranges={[]}
            preventOverflow
            block
          />
        </FilterField>
      </FilterToolbar>

      {viewMode === 'board' ? (
        <ExpensePayrollKanban
          expenses={filteredExpenses}
          currentEmployeeId={currentEmployeeId}
          onOpenExpense={openReviewExpense}
          onApprove={openReviewExpense}
          onMarkPaid={openMarkPaid}
          onMarkInvoiced={openMarkInvoiced}
          onEditInvoiceCap={openMarkInvoiced}
          onRevertStage={revertExpenseStage}
          onToggleQboReceipt={toggleQboReceipt}
        />
      ) : (
      <>
      <div className="approvals-page__table-card">
        <div className="approvals-page__section-title">Expenses Pending Approval</div>
        <ExpenseList
          expenses={pendingApprovalExpenses}
          onView={openReviewExpense}
          onReview={openReviewExpense}
          onCancel={() => {}}
          showEmployeeInfo
          showPaidOn={false}
          showCancelAction={false}
          actionsColumnWidth={170}
        />
        <div className="approvals-page__table-footer">
          Showing {pendingApprovalExpenses.length} request{pendingApprovalExpenses.length === 1 ? '' : 's'}
        </div>
      </div>

      <div className="approvals-page__table-card">
        <div className="approvals-page__section-title">Approved, Awaiting Reimbursement</div>
        <ExpenseList
          expenses={awaitingPaymentExpenses}
          onView={openReviewExpense}
          onMarkPaid={openMarkPaid}
          onCancel={() => {}}
          showEmployeeInfo
          showPaidOn
          showApprovalActions={false}
          showCancelAction={false}
          actionsColumnWidth={140}
        />
        <div className="approvals-page__table-footer">
          Showing {awaitingPaymentExpenses.length} request{awaitingPaymentExpenses.length === 1 ? '' : 's'}
        </div>
      </div>

      <div className="approvals-page__table-card">
        <div className="approvals-page__section-title">Approved and Paid</div>
        <ExpenseList
          expenses={approvedPaidExpenses}
          onView={openReviewExpense}
          onMarkInvoiced={openMarkInvoiced}
          onToggleQboReceipt={toggleQboReceipt}
          onCancel={() => {}}
          showEmployeeInfo
          showPaidOn
          showQboReceipt
          showApprovalActions={false}
          showCancelAction={false}
          actionsColumnWidth={150}
        />
        <div className="approvals-page__table-footer">
          Showing {approvedPaidExpenses.length} request{approvedPaidExpenses.length === 1 ? '' : 's'}
        </div>
      </div>

      <div className="approvals-page__table-card">
        <div className="approvals-page__section-title">Invoiced</div>
        <ExpenseList
          expenses={invoicedExpenses}
          onView={openReviewExpense}
          onToggleQboReceipt={toggleQboReceipt}
          onCancel={() => {}}
          showEmployeeInfo
          showPaidOn
          showInvoicedOn
          showInvoiceCap
          showQboReceipt
          onEditInvoiceCap={openMarkInvoiced}
          showActions={false}
          showApprovalActions={false}
          showCancelAction={false}
        />
        <div className="approvals-page__table-footer">
          Showing {invoicedExpenses.length} request{invoicedExpenses.length === 1 ? '' : 's'}
        </div>
      </div>
      </>
      )}

      <ApprovalReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        record={activeRecord}
        recordType="expense"
        approvalBlockedReason={currentEmployeeId && String(activeRecord?.employee_id || '') === currentEmployeeId
          ? 'You cannot approve your own request'
          : null}
        onApprove={submitApprove}
        onReject={submitReject}
        onSaveComment={submitComment}
        onPrintReceipt={handlePrintReceipt}
        onToggleQboReceipt={toggleQboReceipt}
        onMarkPaid={openMarkPaid}
        loading={approveExpenseMutation.isPending || rejectExpenseMutation.isPending || markExpensePaidMutation.isPending}
        commentLoading={commentExpenseMutation.isPending}
        qboReceiptLoading={setQboReceiptMutation.isPending}
        markPaidLoading={markExpensePaidMutation.isPending}
      />

      <ExpenseReceiptModal
        open={receiptOpen}
        expense={activeRecord}
        onCancel={handleReceiptCancel}
        onClose={() => setReceiptOpen(false)}
      />

      <ExpenseMarkPaidModal
        open={Boolean(paidExpense)}
        value={paidOnDate}
        onChange={setPaidOnDate}
        onSubmit={submitMarkPaid}
        onClose={closeMarkPaid}
        loading={markExpensePaidMutation.isPending}
      />

      <Modal open={Boolean(invoicedExpense)} onClose={closeMarkInvoiced} onExited={resetMarkInvoiced} size="xs">
        <Modal.Header>
          <Modal.Title>{isCapEdit ? 'Edit Invoice Cap' : 'Mark Expense Invoiced'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Named here because the invoice is raised against this customer,
              and the board is where an expense is most easily mixed up. */}
          {invoicedShown?.customer_name ? (
            <div className="text-muted small mb-2">
              Billed to <strong>{invoicedShown.customer_name}</strong>
            </div>
          ) : null}

          {/* Once the invoice is recorded its date is settled, so it is shown
              as context rather than offered for editing — the cap is the one
              thing this dialog reopens to change. */}
          {isCapEdit ? (
            <div className="text-muted small mb-2">
              Invoiced <strong>{formatDate(invoicedShown?.invoiced_on)}</strong>
              {invoiceCapMax > 0 ? <> · we paid <strong>{formatCurrency(invoiceCapMax)}</strong></> : null}
            </div>
          ) : (
            <>
              <div className="text-muted small mb-1">Invoice Date</div>
              <DatePicker
                value={invoicedOnDate}
                onChange={setInvoicedOnDate}
                oneTap
                cleanable={false}
                isoWeek={false}
                weekStart={0}
                format="MM/dd/yyyy"
                style={{ width: '100%' }}
              />
            </>
          )}

          {/* Asked here because this is the moment the money is billed and the
              only moment anyone knows what the contract let us bill for it. The
              expense keeps its own amount either way — the cap is recorded
              beside it, not in place of it. */}
          <div className={isCapEdit ? '' : 'mt-3'}>
            <Checkbox
              checked={invoiceCapped}
              onChange={(_value, checked) => {
                setInvoiceCapped(checked);
                if (!checked) changeInvoiceCap('');
              }}
            >
              Affected by an invoice cap
            </Checkbox>
          </div>

          {invoiceCapped ? (
            <div className="mt-1">
              <div className="text-muted small mb-1">
                Amount we were allowed to invoice
                {!isCapEdit && invoiceCapMax > 0 ? ` (we paid ${formatCurrency(invoiceCapMax)})` : ''}
              </div>
              <Input
                value={invoiceCapDisplay}
                onChange={changeInvoiceCap}
                onBlur={settleInvoiceCap}
                placeholder="$0.00"
                inputMode="decimal"
                className={invoiceCapError ? 'is-invalid' : ''}
              />
              {invoiceCapError ? (
                <div className="text-danger small mt-1">{invoiceCapError}</div>
              ) : null}
            </div>
          ) : null}
        </Modal.Body>
        {/* Clicking an invoiced card comes straight here, so View Expense is the
            way back to everything else about the expense. */}
        <Modal.Footer
          left={isCapEdit ? (
            <Button onClick={viewInvoicedExpense} disabled={invoicedSaving}>
              View Expense
            </Button>
          ) : null}
        >
          <Button onClick={closeMarkInvoiced} disabled={invoicedSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submitMarkInvoiced}
            disabled={!canSubmitInvoiced}
            loading={invoicedSaving}
          >
            {isCapEdit ? 'Save Cap' : 'Mark Invoiced'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
